import { range } from 'lodash'

import { useExecuteCommandMutation } from '@redux/api'

import { ListQueryResponse } from '@hooks'
import { getCommandRequestData } from '@helpers'
import { TIME_LINE_PAGINATION_SIZE, TIME_LINE_URL_SIZE } from '@constants'
import {
  ExecuteCommandPayload,
  ObjectDataRecord,
  POSTObjectDataFilter,
  TimeLineDTOType,
} from '@types'

import {
  getAllPagesData,
  getExpandsParamForObjectX,
  getFilterParamForObjectX,
  getObjectXSAExtraRsqlFilters,
} from '../helpers'

type GetDataFromObjectXCommandType = {
  config: TimeLineDTOType
  startDate?: string | null
  endDate?: string | null
  objectYKeyFieldsValues?: unknown[]
  objectXSARSQLFilter: string | null
  objectYSAFiltersCriteria?: POSTObjectDataFilter
  objectXColumnsObjectSAFiltersCriteria?: POSTObjectDataFilter
}

export const useGetDataFromObjectXCommand = () => {
  const [executeCommand] = useExecuteCommandMutation()

  const getData = async (props: GetDataFromObjectXCommandType) => {
    const {
      config,
      startDate,
      endDate,
      objectYKeyFieldsValues = [],
      objectXSARSQLFilter,
      objectYSAFiltersCriteria,
      objectXColumnsObjectSAFiltersCriteria,
    } = props

    const objectXCommand = config.objectX.getDataCommand
    const objectXParams = objectXCommand?.parameters
      ? getCommandRequestData(objectXCommand.parameters, {})
      : {}

    const objectXParamsExpands =
      typeof objectXParams['expands'] === 'string' ? objectXParams['expands']?.split(',') : []

    const saExtraFilters = getObjectXSAExtraRsqlFilters({
      config,
      objectYSAFiltersCriteria,
      objectXColumnsObjectSAFiltersCriteria,
    })

    const requests = range(
      0,
      objectYKeyFieldsValues.length ? objectYKeyFieldsValues.length / TIME_LINE_URL_SIZE : 1
    ).map(chunk => {
      const commandParams: ExecuteCommandPayload = {
        objectCode: config.objectX.objectCode as string,
        commandCode: objectXCommand?.name as string,
        transformResponseWithPagination: true,
        params: {
          ...objectXParams,
          filter: [
            getFilterParamForObjectX({
              config,
              objectYKeyFieldsValues: objectYKeyFieldsValues.slice(
                chunk * TIME_LINE_URL_SIZE,
                chunk * TIME_LINE_URL_SIZE + TIME_LINE_URL_SIZE
              ),
              startDate,
              endDate,
              saRSQLFilter: objectXSARSQLFilter,
              commandParamsFilter: objectXParams?.filter as string,
            }),
            ...saExtraFilters,
          ]
            .filter(Boolean)
            .join(';'),
          expands: getExpandsParamForObjectX(config).concat(objectXParamsExpands),
        },
        size: TIME_LINE_PAGINATION_SIZE,
        method: objectXCommand?.httpMethod,
        body:
          objectXCommand?.body && objectXCommand?.httpMethod !== 'GET'
            ? getCommandRequestData(objectXCommand.body, {})
            : undefined,
      }

      return executeCommand(commandParams)
        .unwrap()
        .then(result =>
          getAllPagesData({
            firstPageData: result as ListQueryResponse<ObjectDataRecord>,
            executeCommand,
            commandParams,
          })
        )
    })

    const results = await Promise.all(requests)

    return results.flat(1)
  }

  return {
    getDataFromObjectXCommand: getData,
  }
}
